import { skipToken } from '@reduxjs/toolkit/query';
import { CommanderAPI } from '../CommanderAPI';
import { useAppSelector } from '../../../../redux/store.model';
import { getMyWorkspaceSelector } from '../../../../redux/workspace/selectors/Workspace.selectors';
import { ITypeRequest } from '../../../../theme/ui/Dashboard/Dashboard.model';
import { M_SECONDS_POOLING_INTERVAL_DATA } from '../../../store/Store.constants';

/**
 * Patch the loading state to consider the response as loading when it's fetching and has no current data.
 * This happening when the hook is not unmounted but the data is being refetched,
 * so it allows to show a loading state in the UI while the data is being updated.
 * @param response
 * @returns
 */
export function patchLoadingState<
  T extends {
    isUninitialized: boolean;
    isFetching: boolean;
    currentData?: unknown;
  }
>(response: T): T & { isLoading: boolean } {
  return {
    ...response,
    isLoading: response.isFetching && response.currentData === undefined,
  };
}
interface IUseCommanderOfflineProps {
  groupId?: string;
  workspaceId?: string;
  typeRequest: ITypeRequest;
}
function useAirConnectStatusCount(props: IUseCommanderOfflineProps) {
  const { typeRequest, groupId, workspaceId } = props;

  const userDevicesAirConnectStatusResponse =
    CommanderAPI.useGetAirConnectStatusCountByUserQuery(undefined, {
      pollingInterval: M_SECONDS_POOLING_INTERVAL_DATA,
      refetchOnMountOrArgChange: true,
      skip: typeRequest !== 'user',
    });

  const groupDevicesAirConnectStatusResponse =
    CommanderAPI.useGetAirConnectStatusCountByGroupQuery(groupId ?? skipToken, {
      pollingInterval: M_SECONDS_POOLING_INTERVAL_DATA,
      refetchOnMountOrArgChange: true,
      skip: typeRequest !== 'group',
    });

  const workspaceDevicesAirConnectStatusResponse =
    CommanderAPI.useGetAirConnectStatusCountByWorkspaceQuery(
      workspaceId ?? skipToken,
      {
        pollingInterval: M_SECONDS_POOLING_INTERVAL_DATA,
        refetchOnMountOrArgChange: true,
        skip: typeRequest !== 'workspace',
      }
    );

  const myWorkspace = useAppSelector(getMyWorkspaceSelector);

  const workspacesDevicesAirConnectStatusResponse =
    CommanderAPI.useGetAirConnectStatusCountByWorkspaceQuery(
      myWorkspace?.id ?? skipToken,
      {
        pollingInterval: M_SECONDS_POOLING_INTERVAL_DATA,
        refetchOnMountOrArgChange: true,
        skip: typeRequest !== 'workspaces',
      }
    );

  switch (typeRequest) {
    case 'user':
      return patchLoadingState(userDevicesAirConnectStatusResponse);
    case 'group':
      return patchLoadingState(groupDevicesAirConnectStatusResponse);
    case 'workspace':
      return patchLoadingState(workspaceDevicesAirConnectStatusResponse);
    case 'workspaces':
      return patchLoadingState(workspacesDevicesAirConnectStatusResponse);
    default:
      return patchLoadingState(userDevicesAirConnectStatusResponse);
  }
}
export default useAirConnectStatusCount;
